<script lang="ts">
  import { mdiOpenInNew } from "@mdi/js"
  import { openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import ActionBox from "@kontextwerk/web-sdk/ui/layout/ActionBox.svelte"
  import type { CustomerImportReviewSummary } from "../../../../../../api/kehrblatt/imports"
  import CustomerImportReviewGroupDetails from "./_modules/customer-import-review-group-details"
  import {
    projectImportReviewGroups,
    reviewItemPreviewText,
    type ImportReviewGroupView,
    type ImportReviewTone,
  } from "../../_shared/_logic/import-review-projection"

  let {
    draftId,
    organizationId = null,
    summary = null,
    onChanged = () => {},
  }: {
    draftId: string
    organizationId?: string | null
    summary?: CustomerImportReviewSummary | null
    onChanged?: () => void
  } = $props()

  const groups = $derived(projectImportReviewGroups(summary))
  const criticalGroups = $derived(groups.filter((group) => group.tone === "critical"))
  const recommendedGroups = $derived(groups.filter((group) => group.tone === "recommended"))
  const infoGroups = $derived(groups.filter((group) => group.tone === "info"))

  const openGroupDetails = (group: ImportReviewGroupView) => {
    openOverlay({
      title: group.title,
      content: CustomerImportReviewGroupDetails,
      isDark: false,
      properties: {
        draftId,
        organizationId,
        group,
        onChanged,
      },
    })
  }
</script>

<div class="group-sections">
  <section class="risk-section critical">
    <h4>Kritisch</h4>
    <p>Diese Punkte sollten vor der ersten automatischen Kontaktaufnahme geprüft werden.</p>
    {@render groupList(criticalGroups, "critical")}
  </section>

  <section class="risk-section recommended">
    <h4>Empfohlen</h4>
    <p>Diese Punkte können später geprüft werden.</p>
    {@render groupList(recommendedGroups, "recommended")}
  </section>

  <section class="risk-section info">
    <h4>Info</h4>
    <p>Diese Hinweise müssen normalerweise nicht bearbeitet werden.</p>
    {@render groupList(infoGroups, "info")}
  </section>
</div>

{#snippet groupList(groups: ImportReviewGroupView[], tone: ImportReviewTone)}
  {#if groups.length === 0}
    <p class="empty">Keine Hinweise in dieser Kategorie.</p>
  {:else}
    <div class="groups">
      {#each groups as group (group.group_key)}
        <ActionBox label={group.title} darkMode={false} modeRed={tone === "critical"}>
          <div class="group-content">
            <p>{group.description}</p>
            <strong>{group.open_count} offen · {group.total_count} gesamt</strong>
          </div>
          {#if group.examples.length > 0}
            <div class="examples">
              {#each group.examples.slice(0, 2) as example (example.id)}
                <p>{reviewItemPreviewText(example)}</p>
              {/each}
            </div>
          {/if}
          {#snippet buttonSnippet()}
            <CTA
              type="secondaryButton"
              content="Öffnen"
              icon={mdiOpenInNew}
              onClick={() => openGroupDetails(group)}
            />
          {/snippet}
        </ActionBox>
      {/each}
    </div>
  {/if}
{/snippet}

<style lang="less">
  .group-sections {
    display: grid;
    gap: 1rem;
    margin-top: 1rem;
  }

  .risk-section {
    border-left: 4px solid #cbd5e1;
    padding-left: 1rem;
    color: var(--text-100);

    h4 {
      margin: 0 0 0.25rem;
      color: var(--text-100);
    }

    p {
      margin: 0 0 0.6rem;
      color: color-mix(in srgb, var(--text-100) 72%, transparent);
    }
  }

  .critical {
    border-left-color: #dc2626;
  }

  .recommended {
    border-left-color: #d97706;
  }

  .info {
    border-left-color: #64748b;
  }

  .groups {
    display: grid;
    gap: 0.75rem;
  }

  .group-content {
    display: grid;
    gap: 0.35rem;

    p {
      margin: 0;
      color: var(--text-100);
      line-height: 1.35;
    }

    strong {
      color: var(--text-100);
    }
  }

  .examples {
    margin: 0.65rem 0 0;
    padding-left: 0;
    display: grid;
    gap: 0.25rem;
    color: var(--text-100);
    line-height: 1.35;

    p {
      margin: 0;
      color: var(--text-100);
    }
  }

  .empty {
    color: color-mix(in srgb, var(--text-100) 68%, transparent);
    margin: 0;
  }
</style>
